import axios from 'axios';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Trash2, Plus, ChevronRight, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// Calcule les jours entre deux dates.
// La date de remise est le jour où l'équipement est rendu : il n'est pas
// "utilisé" ce jour-là. Donc 18 mai → 1 juin = 14 jours (= 2 semaines).
function calcJours(datePrise, dateRemise) {
  if (!datePrise || !dateRemise) return null;
  const d1 = new Date(datePrise);
  const d2 = new Date(dateRemise);
  if (d2 < d1) return null;
  return Math.max(1, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
}

// ──────────────────────────────────────────────────────────────────
// Nouveau composant unifié Nacelles + Échafauds
// ──────────────────────────────────────────────────────────────────
export function SectionEquipements({ items, onItemsChange }) {
  const [afficherSousTotal, setAfficherSousTotal] = useState(true);
  const [inv, setInv] = useState({ nacelles: [], echafauds_pieces: [], echafauds_templates: [] });

  const fetchInv = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventaire-locatif`);
      if (res.status === 200) setInv(res.data || { nacelles: [], echafauds_pieces: [], echafauds_templates: [] });
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchInv(); }, [fetchInv]);

  const recalculerLigne = (item) => {
    const jours = calcJours(item.date_prise, item.date_remise);
    // Quantité manuelle saisie par l'utilisateur (prime sur le calcul auto)
    const qteManuelle = item.quantite_manuelle !== undefined && item.quantite_manuelle !== null && item.quantite_manuelle !== ''
      ? parseFloat(item.quantite_manuelle)
      : null;

    // Calcul de quantité auto selon la durée
    const calcQteAuto = () => {
      if (jours === null) return parseFloat(item.quantite) || 1;
      if (item.duree_type === 'semaine') return Math.max(1, Math.ceil(jours / 7));
      if (item.duree_type === 'mois') return Math.max(1, Math.ceil(jours / 30));
      return jours; // jour
    };
    let quantite = qteManuelle && !isNaN(qteManuelle) && qteManuelle > 0 ? qteManuelle : calcQteAuto();

    let prixLocation = parseFloat(item.prix_location_override ?? 0);
    let cappedToMonth = false;

    // Si pas d'override manuel, on calcule depuis l'inventaire
    if (!item.prix_location_override && item.type_location === 'nacelle' && item.nacelle_id) {
      const eq = inv.nacelles.find(n => n.id === item.nacelle_id);
      if (eq) {
        const prixJour = eq.prix_jour || 0, prixSem = eq.prix_semaine || 0, prixMois = eq.prix_mois || 0;
        if (item.duree_type === 'semaine') {
          prixLocation = prixSem;
        } else if (item.duree_type === 'mois') {
          prixLocation = prixMois;
        } else {
          const totalJour = prixJour * quantite;
          if (prixSem > 0 && totalJour > prixSem) { prixLocation = prixSem; quantite = 1; }
          else prixLocation = prixJour;
        }

        // ✅ Plafonnement automatique au tarif mensuel
        // Si X jours/semaines coûtent plus cher que N mois, on bascule au tarif mois.
        if (prixMois > 0 && item.duree_type !== 'mois' && jours !== null && !qteManuelle) {
          const nbMoisCouverts = Math.max(1, Math.ceil(jours / 30));
          const totalActuel = prixLocation * quantite;
          const totalMois = prixMois * nbMoisCouverts;
          if (totalActuel > totalMois) {
            prixLocation = prixMois;
            quantite = nbMoisCouverts;
            cappedToMonth = true;
          }
        }
      }
    } else if (item.type_location === 'echafauds') {
      // Pour échafauds, on somme le prix de chaque pièce × sa quantité
      const pieces = item.pieces_echafauds || [];
      const calcPrixUnit = (key) => pieces.reduce((sum, p) => {
        const invPiece = inv.echafauds_pieces.find(x => (x.id === p.piece_id || x.nom === p.piece_nom));
        if (!invPiece) return sum;
        return sum + ((invPiece[key] || 0) * (p.qte || 0));
      }, 0);
      const prixUnitJour = calcPrixUnit('prix_jour');
      const prixUnitSem = calcPrixUnit('prix_semaine');
      const prixUnitMois = calcPrixUnit('prix_mois');
      if (item.duree_type === 'semaine') prixLocation = prixUnitSem;
      else if (item.duree_type === 'mois') prixLocation = prixUnitMois;
      else prixLocation = prixUnitJour;

      // ✅ Plafonnement automatique au tarif mensuel pour échafauds aussi
      if (prixUnitMois > 0 && item.duree_type !== 'mois' && jours !== null && !qteManuelle) {
        const nbMoisCouverts = Math.max(1, Math.ceil(jours / 30));
        const totalActuel = prixLocation * quantite;
        const totalMois = prixUnitMois * nbMoisCouverts;
        if (totalActuel > totalMois) {
          prixLocation = prixUnitMois;
          quantite = nbMoisCouverts;
          cappedToMonth = true;
        }
      }
    }

    // Mode livraison :
    //   'bureau' → ancien comportement : prise + remise au bureau, aucun frais
    //   'livraison' → frais auto-calculés selon prise_mode + remise_mode
    //
    // ✅ 2026-04-27 (règle auto) :
    //   - prise_mode = 'bureau' : le client vient chercher → 0 trajet pour nous
    //   - prise_mode = 'livraison' : on va livrer → 1 trajet
    //   - remise_mode = 'bureau' : le client ramène → 0 trajet
    //   - remise_mode = 'livraison' : on va chercher → 1 trajet
    //   nb_trajets = (prise=='livraison') + (remise=='livraison')
    //   sens = (nb_trajets === 2) ? aller_retour : aller_seulement
    let prixLivraison = 0;
    let livraison_km_montant = 0;
    let livraison_endroit_montant = 0;
    let livraison_sens_calcule = 'aller';
    let nb_trajets = 0;
    if (item.mode_livraison === 'livraison') {
      // Rétrocompat : si prise_mode/remise_mode pas définis, on suppose les 2 livrés (aller-retour)
      const prise = item.prise_mode || 'livraison';
      const remise = item.remise_mode || 'livraison';
      nb_trajets = (prise === 'livraison' ? 1 : 0) + (remise === 'livraison' ? 1 : 0);
      livraison_sens_calcule = nb_trajets === 2 ? 'aller_retour' : 'aller';

      const sens = livraison_sens_calcule === 'aller_retour' ? '_ar' : '';
      const pickKm = (liv) => {
        if (item.livraison_km === '0-55') return liv[`km_0_55${sens}`] || liv.km_0_55 || 0;
        if (item.livraison_km === '55-80') return liv[`km_55_80${sens}`] || liv.km_55_80 || 0;
        if (item.livraison_km === '80+') return liv[`km_80_plus${sens}`] || liv.km_80_plus || 0;
        return 0;
      };
      let liv = {};
      if (item.type_location === 'nacelle' && item.nacelle_id) {
        const eq = inv.nacelles.find(n => n.id === item.nacelle_id);
        liv = eq?.livraison || {};
      } else if (item.type_location === 'echafauds') {
        const refNacelle = inv.nacelles.find(n => (n.livraison || {}).km_0_55 !== undefined);
        liv = refNacelle?.livraison || {};
      }

      // Si 0 trajet, frais = 0 (les 2 sont au bureau)
      livraison_km_montant = nb_trajets > 0 ? pickKm(liv) : 0;
      if (nb_trajets > 0) {
        if (item.livraison_endroit === 'precise') livraison_endroit_montant = liv.prix_livraison_precise || 0;
        else if (item.livraison_endroit === 'adresse') livraison_endroit_montant = liv.prix_livraison_adresse || 0;
        // ✅ Point 4 : si surcharge "endroit précis" demandée par le user → ajouter
        if (item.livraison_endroit === 'precise' && item.livraison_endroit_supplement != null && item.livraison_endroit_supplement !== '') {
          livraison_endroit_montant += parseFloat(item.livraison_endroit_supplement) || 0;
        }
      }
      prixLivraison = livraison_km_montant + livraison_endroit_montant;
    }

    // ✅ Point 5 : extras avec descriptions + montants individuels
    const extras = item.extras || [];
    const extrasTotal = extras.reduce((s, ex) => s + (parseFloat(ex.montant) || 0), 0);
    const montant = (prixLocation * quantite) + prixLivraison + extrasTotal;
    return {
      ...item,
      quantite,
      prix_location: prixLocation,
      prix_livraison: prixLivraison,
      livraison_km_montant,
      livraison_endroit_montant,
      livraison_sens: livraison_sens_calcule,  // ✅ AUTO-CALCULÉ
      nb_trajets,
      extras_total: extrasTotal,
      montant,
      capped_to_month: cappedToMonth,
      // Si plafonné, on aligne le duree_type affiché sur 'mois' pour clarté
      duree_type: cappedToMonth ? 'mois' : (item.duree_type || 'jour'),
    };
  };

  const patchItem = (idx, patch) => {
    const updated = [...items];
    updated[idx] = recalculerLigne({ ...updated[idx], ...patch });
    onItemsChange(updated);
  };

  const ajouterLigne = () => {
    onItemsChange([
      ...items,
      {
        type_location: '',            // 'nacelle' ou 'echafauds'
        // Nacelle
        nacelle_id: '',
        // Échafauds
        echafauds_mode: 'etages',    // 'etages' ou 'pieces'
        echafauds_template_id: '',
        echafauds_nb_etages: 3,
        pieces_echafauds: [],         // [{ piece_id, piece_nom, qte }]
        // Commun
        description: '',
        date_prise: '',
        date_remise: '',
        duree_type: 'jour',
        quantite: 1,
        mode_livraison: 'bureau',    // 'bureau' (pas de transport) ou 'livraison'
        prise_mode: 'livraison',     // ✅ AUTO : 'bureau' (client vient) ou 'livraison' (on livre)
        remise_mode: 'livraison',    // ✅ AUTO : 'bureau' (client ramène) ou 'livraison' (on récupère)
        livraison_km: '',
        livraison_endroit: '',
        livraison_sens: 'aller_retour',   // calculé auto, conservé pour PDF/historique
        livraison_endroit_supplement: '',  // ✅ Point 4 : surcharge optionnelle
        prix_location: 0,
        prix_livraison: 0,
        extras: [],                         // ✅ Point 5 : [{ description, montant }]
        montant: 0,
        notes: '',                          // 🛠️ [Phase 21 R3] Notes par équipement (restauré)
      },
    ]);
  };

  const supprimerLigne = (idx) => onItemsChange(items.filter((_, i) => i !== idx));

  const sousTotal = items.reduce((s, i) => s + (parseFloat(i.montant) || 0), 0);

  return (
    <Card data-testid="section-equipements">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-2xl text-center flex-1 font-bold">Équipements à louer</CardTitle>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="sous-total-eq" checked={afficherSousTotal} onChange={(e) => setAfficherSousTotal(e.target.checked)} className="w-4 h-4" />
            <label htmlFor="sous-total-eq" className="text-sm cursor-pointer">Afficher sous-total</label>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-3">
          {items.map((item, idx) => (
            <LigneLocation
              key={idx}
              idx={idx}
              item={item}
              inv={inv}
              onPatch={(patch) => patchItem(idx, patch)}
              onDelete={() => supprimerLigne(idx)}
            />
          ))}

          {afficherSousTotal && items.length > 0 && (
            <div className="flex justify-end border-t-2 border-slate-300 pt-2">
              <div className="bg-slate-100 px-4 py-2 rounded font-bold">
                Sous-total : <span className="text-lg ml-2">{sousTotal.toFixed(2)} $</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4">
          <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white w-full" onClick={ajouterLigne} data-testid="add-equipement-line-btn">
            + Ajouter un équipement à louer
          </Button>
        </div>

        {items.length > 0 && (
          <p className="mt-4 text-sm font-medium text-gray-700 italic border-t pt-3" data-testid="assurance-notice">
            * 15% de frais d'assurances pour bris d'équipements
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────
// Ligne d'un équipement à louer (nacelle OU échafauds)
// ──────────────────────────────────────────────────────────────────
function LigneLocation({ idx, item, inv, onPatch, onDelete }) {
  const [piecesOpen, setPiecesOpen] = useState(true);
  const [dispo, setDispo] = useState(null);

  // Vérification de disponibilité temps réel
  useEffect(() => {
    if (!item.type_location || !item.date_prise || !item.date_remise) {
      setDispo(null);
      return;
    }
    const itemsBody = [];
    if (item.type_location === 'nacelle' && item.nacelle_id) {
      itemsBody.push({ type: 'nacelle', id: item.nacelle_id, qte: 1 });
    } else if (item.type_location === 'echafauds') {
      for (const p of (item.pieces_echafauds || [])) {
        if (p.piece_nom && p.qte > 0) {
          itemsBody.push({ type: 'echafauds', piece_nom: p.piece_nom, qte: p.qte });
        }
      }
    }
    if (itemsBody.length === 0) { setDispo(null); return; }

    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API_URL}/api/location/verifier-disponibilite`, {
          date_debut: item.date_prise,
          date_fin: item.date_remise,
          items: itemsBody,
        });
        if (!cancel) setDispo(res.data);
      } catch (e) {
        if (!cancel) setDispo(null);
      }
    }, 400);
    return () => { cancel = true; clearTimeout(t); };
  }, [
    item.type_location,
    item.date_prise,
    item.date_remise,
    item.nacelle_id,
    JSON.stringify(item.pieces_echafauds || []),
  ]);

  const templateChoisi = useMemo(
    () => inv.echafauds_templates.find(t => t.id === item.echafauds_template_id),
    [inv.echafauds_templates, item.echafauds_template_id]
  );

  // Si l'utilisateur choisit un template + nb étages, on génère la liste des pièces automatiquement
  const genererPiecesDepuisTemplate = (tplId, nbEtages) => {
    const tpl = inv.echafauds_templates.find(t => t.id === tplId);
    if (!tpl) return [];
    const cumul = {};
    const add = (pieceRef, qte) => {
      if (!pieceRef || pieceRef === '__empty__') return;
      cumul[pieceRef] = (cumul[pieceRef] || 0) + qte;
    };
    for (let n = 1; n <= nbEtages; n++) {
      (tpl.composition || []).forEach(c => {
        const ref = c.piece_id || c.piece;
        const qte = c.quantite_par_etage || c.quantite || 0;
        add(ref, qte);
      });
      if (n === 1) (tpl.addon_premier || []).forEach(c => add(c.piece_id || c.piece, c.quantite || 0));
      if (n === nbEtages) {
        (tpl.addon_dernier || []).forEach(c => add(c.piece_id || c.piece, c.quantite || 0));
        (tpl.addon_acces || []).forEach(c => add(c.piece_id || c.piece, c.quantite || 0));
      }
    }
    return Object.entries(cumul).map(([ref, qte]) => {
      const p = inv.echafauds_pieces.find(x => x.id === ref || x.nom === ref);
      return { piece_id: p?.id || ref, piece_nom: p?.nom || ref, qte };
    });
  };

  const onChangeTemplate = (tplId) => {
    const pieces = genererPiecesDepuisTemplate(tplId, item.echafauds_nb_etages || 3);
    const tpl = inv.echafauds_templates.find(t => t.id === tplId);
    onPatch({
      echafauds_template_id: tplId,
      pieces_echafauds: pieces,
      description: tpl ? `Échafauds ${tpl.nom} — ${item.echafauds_nb_etages || 3} étage(s)` : item.description,
    });
  };

  const onChangeNbEtages = (nb) => {
    const n = Math.max(1, parseInt(nb, 10) || 1);
    const pieces = item.echafauds_template_id ? genererPiecesDepuisTemplate(item.echafauds_template_id, n) : [];
    const tpl = inv.echafauds_templates.find(t => t.id === item.echafauds_template_id);
    onPatch({
      echafauds_nb_etages: n,
      pieces_echafauds: pieces,
      description: tpl ? `Échafauds ${tpl.nom} — ${n} étage(s)` : item.description,
    });
  };

  const patchPiece = (pIdx, patch) => {
    const pieces = [...(item.pieces_echafauds || [])];
    pieces[pIdx] = { ...pieces[pIdx], ...patch };
    onPatch({ pieces_echafauds: pieces });
  };

  const supprimerPiece = (pIdx) => {
    onPatch({ pieces_echafauds: (item.pieces_echafauds || []).filter((_, i) => i !== pIdx) });
  };

  const ajouterPiece = () => {
    onPatch({ pieces_echafauds: [...(item.pieces_echafauds || []), { piece_id: '', piece_nom: '', qte: 1 }] });
  };

  // Nacelle changée → description auto (✅ 2026-04-27 : SANS émoticône, le client n'a pas à voir 🏗)
  const onChangeNacelle = (id) => {
    const eq = inv.nacelles.find(n => n.id === id);
    const label = eq ? `${eq.nom}${eq.numero_unite ? ` (#${eq.numero_unite})` : ''}${eq.annee ? ` — ${eq.annee}` : ''}`.trim() : '';
    onPatch({ nacelle_id: id, description: label });
  };

  const joursCalc = calcJours(item.date_prise, item.date_remise);

  // ✅ Comparateur de tarifs jour/semaine/mois — calcule les 3 totaux pour la durée
  const tarifs = useMemo(() => {
    if (!item.type_location) return null;
    let pj = 0, ps = 0, pm = 0;
    if (item.type_location === 'nacelle' && item.nacelle_id) {
      const eq = inv.nacelles.find(n => n.id === item.nacelle_id);
      if (!eq) return null;
      pj = eq.prix_jour || 0; ps = eq.prix_semaine || 0; pm = eq.prix_mois || 0;
    } else if (item.type_location === 'echafauds') {
      const pieces = item.pieces_echafauds || [];
      const sumKey = (k) => pieces.reduce((s, p) => {
        const ip = inv.echafauds_pieces.find(x => x.id === p.piece_id || x.nom === p.piece_nom);
        return s + ((ip?.[k] || 0) * (p.qte || 0));
      }, 0);
      pj = sumKey('prix_jour'); ps = sumKey('prix_semaine'); pm = sumKey('prix_mois');
    }
    if (joursCalc === null) return { pj, ps, pm, totals: null };
    return {
      pj, ps, pm,
      totals: {
        jour:    pj * joursCalc,
        semaine: ps * Math.max(1, Math.ceil(joursCalc / 7)),
        mois:    pm * Math.max(1, Math.ceil(joursCalc / 30)),
      },
      qtes: {
        jour:    joursCalc,
        semaine: Math.max(1, Math.ceil(joursCalc / 7)),
        mois:    Math.max(1, Math.ceil(joursCalc / 30)),
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.type_location, item.nacelle_id, JSON.stringify(item.pieces_echafauds || []), joursCalc, inv]);

  return (
    <div className="border-2 border-slate-200 rounded-lg p-3 bg-slate-50 space-y-3" data-testid={`ligne-loc-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <Badge variant="outline" className="text-[10px]">#{idx + 1}</Badge>
          {/* Sélecteur TYPE */}
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => onPatch({ type_location: 'nacelle' })}
              className={`px-3 py-1 rounded text-xs font-semibold border transition ${
                item.type_location === 'nacelle'
                  ? 'bg-cyan-600 text-white border-cyan-700'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
              }`}
              data-testid={`tlc-nacelle-${idx}`}
            >
              🏗 Nacelle
            </button>
            <button
              type="button"
              onClick={() => onPatch({ type_location: 'echafauds' })}
              className={`px-3 py-1 rounded text-xs font-semibold border transition ${
                item.type_location === 'echafauds'
                  ? 'bg-orange-600 text-white border-orange-700'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
              }`}
              data-testid={`tlc-echafauds-${idx}`}
            >
              📐 Échafauds
            </button>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500 hover:bg-red-50 h-7 w-7 p-0">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* ─── Zone de choix selon type ─── */}
      {item.type_location === 'nacelle' && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-600">Choisir la nacelle</label>
          <Select value={item.nacelle_id || ''} onValueChange={onChangeNacelle}>
            <SelectTrigger className="h-9 bg-white" data-testid={`nacelle-select-${idx}`}>
              <SelectValue placeholder="Choisir une unité spécifique..." />
            </SelectTrigger>
            <SelectContent>
              {inv.nacelles.length === 0 && <SelectItem value="__none" disabled>Aucune nacelle dispo</SelectItem>}
              {inv.nacelles.map(n => (
                <SelectItem key={n.id} value={n.id} disabled={!n.disponible}>
                  {n.emoji || '🏗'} {n.nom} {n.numero_unite ? `— #${n.numero_unite}` : ''}
                  {n.hauteur_travail ? ` · ${n.hauteur_travail}` : ''}
                  {!n.disponible ? ' (indisponible)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {item.type_location === 'echafauds' && (
        <div className="space-y-2">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => onPatch({ echafauds_mode: 'etages' })}
              className={`px-3 py-1 rounded text-xs font-semibold border transition ${
                item.echafauds_mode === 'etages'
                  ? 'bg-orange-500 text-white border-orange-600'
                  : 'bg-white text-slate-600 border-slate-300'
              }`}
              data-testid={`ech-mode-etages-${idx}`}
            >
              Par nombre d'étages
            </button>
            <button
              type="button"
              onClick={() => onPatch({ echafauds_mode: 'pieces', echafauds_template_id: '', pieces_echafauds: [] })}
              className={`px-3 py-1 rounded text-xs font-semibold border transition ${
                item.echafauds_mode === 'pieces'
                  ? 'bg-orange-500 text-white border-orange-600'
                  : 'bg-white text-slate-600 border-slate-300'
              }`}
              data-testid={`ech-mode-pieces-${idx}`}
            >
              Pièces séparées
            </button>
          </div>

          {item.echafauds_mode === 'etages' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-600">Type d'étage</label>
                <Select value={item.echafauds_template_id || ''} onValueChange={onChangeTemplate}>
                  <SelectTrigger className="h-9 bg-white">
                    <SelectValue placeholder="Choisir type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {inv.echafauds_templates.length === 0 && <SelectItem value="__none" disabled>Aucun template</SelectItem>}
                    {inv.echafauds_templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.nom} {t.dimensions ? `(${t.dimensions})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600">Nombre d'étages</label>
                <Input
                  type="number" min="1" max="10"
                  value={item.echafauds_nb_etages || ''}
                  onChange={(e) => onChangeNbEtages(e.target.value)}
                  className="h-9 bg-white"
                />
              </div>
            </div>
          )}

          {/* Liste des pièces (éditable si mode pieces, sinon preview depuis template) */}
          {(item.pieces_echafauds || []).length > 0 || item.echafauds_mode === 'pieces' ? (
            <div className="bg-white border border-slate-200 rounded p-2">
              <button
                type="button"
                onClick={() => setPiecesOpen(o => !o)}
                className="w-full flex items-center justify-between text-xs font-semibold text-slate-700 mb-1"
              >
                <span>
                  {piecesOpen ? <ChevronDown className="w-3 h-3 inline" /> : <ChevronRight className="w-3 h-3 inline" />}
                  Pièces ({(item.pieces_echafauds || []).length})
                  {item.echafauds_mode === 'etages' && templateChoisi && (
                    <span className="text-slate-400 font-normal ml-2">— modifiable</span>
                  )}
                </span>
                <Button
                  type="button" size="sm" variant="outline"
                  className="h-6 text-[10px] border-orange-300"
                  onClick={(e) => { e.stopPropagation(); ajouterPiece(); }}
                >
                  <Plus className="w-2.5 h-2.5 mr-0.5" /> Pièce
                </Button>
              </button>
              {piecesOpen && (
                <div className="space-y-1">
                  {(item.pieces_echafauds || []).map((p, pIdx) => (
                    <div key={pIdx} className="flex items-center gap-1.5">
                      <Input
                        type="number" min="1"
                        value={p.qte || ''}
                        onChange={(e) => patchPiece(pIdx, { qte: parseInt(e.target.value, 10) || 0 })}
                        className="h-7 text-xs w-14" placeholder="Qté"
                      />
                      <Select
                        value={p.piece_id || p.piece_nom || '__empty__'}
                        onValueChange={(v) => {
                          const inv_p = inv.echafauds_pieces.find(x => x.id === v || x.nom === v);
                          patchPiece(pIdx, { piece_id: inv_p?.id || v, piece_nom: inv_p?.nom || v });
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs flex-1">
                          <SelectValue placeholder="Pièce..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__empty__">— Choisir —</SelectItem>
                          {inv.echafauds_pieces.filter(x => x.nom).map((x, i) => (
                            <SelectItem key={i} value={x.id || x.nom}>
                              {x.nom} {x.prix_jour ? `(${x.prix_jour}$/j)` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400"
                        onClick={() => supprimerPiece(pIdx)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  {(item.pieces_echafauds || []).length === 0 && (
                    <p className="text-[11px] text-slate-400 italic py-1">Aucune pièce — ajoutez-en avec le bouton ci-dessus.</p>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* ─── Dates + durée ─── */}
      {item.type_location && (
        <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div>
            <label className="text-xs text-slate-600">Prise de possession</label>
            <Input type="date" className="h-9 bg-white" value={item.date_prise || ''}
              onChange={(e) => onPatch({ date_prise: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-slate-600">Remise</label>
            <Input type="date" className="h-9 bg-white" value={item.date_remise || ''}
              onChange={(e) => onPatch({ date_remise: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-slate-600">Tarif</label>
            <Select value={item.duree_type || 'jour'} onValueChange={(v) => onPatch({ duree_type: v })}>
              <SelectTrigger className="h-9 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="jour">À la journée</SelectItem>
                <SelectItem value="semaine">À la semaine</SelectItem>
                <SelectItem value="mois">Au mois</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-600 flex items-center justify-between">
              <span>Qté {joursCalc !== null && <span className="text-emerald-600">({joursCalc}j)</span>}</span>
              {item.quantite_manuelle !== undefined && item.quantite_manuelle !== null && item.quantite_manuelle !== '' && (
                <button
                  type="button"
                  onClick={() => onPatch({ quantite_manuelle: '' })}
                  className="text-[10px] text-blue-600 hover:underline"
                  data-testid={`reset-qte-${idx}`}
                  title="Revenir au calcul automatique"
                >
                  ↻ auto
                </button>
              )}
            </label>
            <Input
              type="number" min="1" step="1" className="h-9 bg-white"
              value={
                item.quantite_manuelle !== undefined && item.quantite_manuelle !== null && item.quantite_manuelle !== ''
                  ? item.quantite_manuelle
                  : (item.quantite || 1)
              }
              onChange={(e) => onPatch({ quantite_manuelle: e.target.value })}
              data-testid={`qte-input-${idx}`}
            />
          </div>
        </div>

        {/* 🛠️ [Phase 21 R3 — 2026-05-31] Notes par équipement loué.
            Régression : le champ avait été retiré dans une refonte antérieure.
            Restauré ici (textarea unique, global à la location). */}
        <div>
          <label className="text-xs text-slate-600">Notes (optionnel)</label>
          <textarea
            className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm bg-white min-h-[60px] focus:outline-none focus:ring-2 focus:ring-cyan-200"
            value={item.notes || ''}
            onChange={(e) => onPatch({ notes: e.target.value })}
            placeholder="Ex : à livrer avant 8h, contact sur place 514-555-1234…"
            rows={2}
            data-testid={`equipement-notes-${idx}`}
          />
        </div>

        {/* ✅ Comparateur de tarifs : jour vs semaine vs mois — surlignage vert du meilleur */}
        {tarifs && tarifs.totals && (tarifs.pj > 0 || tarifs.ps > 0 || tarifs.pm > 0) && (
          <div className="mt-2" data-testid={`comparateur-tarifs-${idx}`}>
            <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1 tracking-wide">
              Comparaison des 3 tarifs pour {joursCalc} jour{joursCalc > 1 ? 's' : ''}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { key: 'jour',    label: 'Jour',    prixUnit: tarifs.pj, total: tarifs.totals.jour,    qte: tarifs.qtes.jour },
                { key: 'semaine', label: 'Semaine', prixUnit: tarifs.ps, total: tarifs.totals.semaine, qte: tarifs.qtes.semaine },
                { key: 'mois',    label: 'Mois',    prixUnit: tarifs.pm, total: tarifs.totals.mois,    qte: tarifs.qtes.mois },
              ].map(t => {
                const valides = [tarifs.totals.jour, tarifs.totals.semaine, tarifs.totals.mois].filter(v => v > 0);
                const min = valides.length ? Math.min(...valides) : 0;
                const isBest = t.total > 0 && t.total === min;
                const isActive = (item.duree_type || 'jour') === t.key;
                const disabled = !t.prixUnit;
                return (
                  <button
                    type="button"
                    key={t.key}
                    disabled={disabled}
                    onClick={() => onPatch({ duree_type: t.key, quantite_manuelle: '' })}
                    className={`text-left rounded-lg border-2 px-2 py-1.5 transition relative ${
                      disabled
                        ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed'
                        : isActive
                          ? 'border-cyan-600 bg-cyan-50 ring-2 ring-cyan-200'
                          : isBest
                            ? 'border-emerald-500 bg-emerald-50 hover:bg-emerald-100'
                            : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                    data-testid={`tarif-card-${t.key}-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] uppercase font-bold tracking-wide ${isBest && !isActive ? 'text-emerald-700' : isActive ? 'text-cyan-700' : 'text-slate-500'}`}>
                        {t.label}
                      </span>
                      {isActive && <span className="text-[9px] text-cyan-700 font-bold">✓ choisi</span>}
                      {isBest && !isActive && <span className="text-[9px] text-emerald-700 font-bold">✓ moins cher</span>}
                    </div>
                    {disabled ? (
                      <div className="text-[11px] text-slate-400 italic mt-0.5">Tarif non défini</div>
                    ) : (
                      <>
                        <div className="text-xs text-slate-600 mt-0.5">{t.prixUnit.toFixed(2)}$ / {t.label.toLowerCase()}</div>
                        <div className="text-xs text-slate-500">× {t.qte} =</div>
                        <div className={`text-base font-bold ${isBest && !isActive ? 'text-emerald-700' : isActive ? 'text-cyan-800' : 'text-slate-800'}`}>
                          {t.total.toFixed(2)}$
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ✅ Indicateur de plafonnement automatique au tarif mensuel */}
        {item.capped_to_month && (
          <div className="bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5 mt-2 text-[11px] text-emerald-800 flex items-center gap-2" data-testid={`capped-month-${idx}`}>
            <span>💡</span>
            <span>
              <strong>Tarif plafonné au prix mensuel</strong> — la durée demandée coûterait plus cher au tarif jour/semaine, donc on applique automatiquement le tarif mois (avantage client).
            </span>
          </div>
        )}

        {/* ✅ Point 2 : Tarif explicite par durée */}
        {item.prix_location > 0 && (
          <div className="bg-cyan-50 border border-cyan-200 rounded px-3 py-2 mt-2 flex items-center justify-between text-xs" data-testid={`tarif-affiche-${idx}`}>
            <span className="font-semibold text-cyan-900">
              Tarif : {item.prix_location.toFixed(2)} $ / {item.duree_type === 'semaine' ? 'semaine' : item.duree_type === 'mois' ? 'mois' : 'jour'}
            </span>
            <span className="text-cyan-700">
              × {item.quantite || 1} = <strong>{((item.prix_location || 0) * (item.quantite || 1)).toFixed(2)} $</strong>
            </span>
          </div>
        )}
        </>
      )}

      {/* ─── Mode de livraison ─── */}
      {item.type_location && (
        <div className="bg-white border border-slate-200 rounded p-2 space-y-2">
          <p className="text-xs font-semibold text-slate-700">Mode de livraison</p>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => onPatch({ mode_livraison: 'bureau', livraison_km: '', livraison_endroit: '' })}
              className={`px-3 py-1 rounded text-xs font-semibold border transition ${
                item.mode_livraison === 'bureau'
                  ? 'bg-emerald-500 text-white border-emerald-600'
                  : 'bg-white text-slate-600 border-slate-300'
              }`}
              data-testid={`liv-bureau-${idx}`}
            >
              🏢 Prise au bureau (sans frais)
            </button>
            <button
              type="button"
              onClick={() => onPatch({ mode_livraison: 'livraison' })}
              className={`px-3 py-1 rounded text-xs font-semibold border transition ${
                item.mode_livraison === 'livraison'
                  ? 'bg-blue-600 text-white border-blue-700'
                  : 'bg-white text-slate-600 border-slate-300'
              }`}
              data-testid={`liv-livraison-${idx}`}
            >
              🚚 Livraison
            </button>
          </div>

          {item.mode_livraison === 'livraison' && (
            <>
            {/* ✅ 2026-04-27 : Prise + Remise (sens calculé automatiquement) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`prise-remise-${idx}`}>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">📥 Prise (début)</label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => onPatch({ prise_mode: 'livraison' })}
                    className={`flex-1 px-2 py-1.5 rounded text-[11px] font-semibold border transition ${
                      (item.prise_mode || 'livraison') === 'livraison'
                        ? 'bg-indigo-600 text-white border-indigo-700'
                        : 'bg-white text-slate-600 border-slate-300'
                    }`}
                    data-testid={`prise-livraison-${idx}`}
                  >
                    🚚 Nous livrons
                  </button>
                  <button
                    type="button"
                    onClick={() => onPatch({ prise_mode: 'bureau' })}
                    className={`flex-1 px-2 py-1.5 rounded text-[11px] font-semibold border transition ${
                      item.prise_mode === 'bureau'
                        ? 'bg-emerald-600 text-white border-emerald-700'
                        : 'bg-white text-slate-600 border-slate-300'
                    }`}
                    data-testid={`prise-bureau-${idx}`}
                  >
                    🏢 Client vient chercher
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">📤 Remise (fin)</label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => onPatch({ remise_mode: 'livraison' })}
                    className={`flex-1 px-2 py-1.5 rounded text-[11px] font-semibold border transition ${
                      (item.remise_mode || 'livraison') === 'livraison'
                        ? 'bg-indigo-600 text-white border-indigo-700'
                        : 'bg-white text-slate-600 border-slate-300'
                    }`}
                    data-testid={`remise-livraison-${idx}`}
                  >
                    🚚 Nous récupérons
                  </button>
                  <button
                    type="button"
                    onClick={() => onPatch({ remise_mode: 'bureau' })}
                    className={`flex-1 px-2 py-1.5 rounded text-[11px] font-semibold border transition ${
                      item.remise_mode === 'bureau'
                        ? 'bg-emerald-600 text-white border-emerald-700'
                        : 'bg-white text-slate-600 border-slate-300'
                    }`}
                    data-testid={`remise-bureau-${idx}`}
                  >
                    🏢 Client ramène
                  </button>
                </div>
              </div>
            </div>

            {/* Indicateur du sens calculé automatiquement */}
            <div
              className={`rounded px-2 py-1.5 text-[11px] font-semibold flex items-center gap-2 ${
                (item.nb_trajets || 0) === 2 ? 'bg-amber-50 border border-amber-200 text-amber-800'
                : (item.nb_trajets || 0) === 1 ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-slate-50 border border-slate-200 text-slate-600'
              }`}
              data-testid={`sens-auto-${idx}`}
            >
              {(item.nb_trajets || 0) === 2 && <>🔄 Détecté : <strong>Aller-retour</strong> (2 trajets) → tarif aller-retour appliqué automatiquement</>}
              {(item.nb_trajets || 0) === 1 && <>➡️ Détecté : <strong>Aller seulement</strong> (1 trajet) → tarif aller seul appliqué automatiquement</>}
              {(item.nb_trajets || 0) === 0 && <>🏢 Aucun trajet — <strong>aucun frais</strong> de livraison facturé</>}
            </div>

            {(item.nb_trajets || 0) > 0 && (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-600">Distance</label>
                <Select value={item.livraison_km || ''} onValueChange={(v) => onPatch({ livraison_km: v })}>
                  <SelectTrigger className="h-9 bg-white"><SelectValue placeholder="Km..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-55">0-55 km</SelectItem>
                    <SelectItem value="55-80">55-80 km</SelectItem>
                    <SelectItem value="80+">80 km +</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600">Endroit</label>
                <Select value={item.livraison_endroit || ''} onValueChange={(v) => onPatch({ livraison_endroit: v })}>
                  <SelectTrigger className="h-9 bg-white"><SelectValue placeholder="Endroit..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="precise">Endroit précis</SelectItem>
                    <SelectItem value="adresse">Devant adresse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ✅ Point 4 : surcharge optionnelle pour "Endroit précis" */}
            {item.livraison_endroit === 'precise' && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2" data-testid={`liv-supplement-${idx}`}>
                <label className="text-xs font-semibold text-amber-900 block mb-1">
                  Supplément "endroit précis" (optionnel)
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={item.livraison_endroit_supplement || ''}
                    onChange={(e) => onPatch({ livraison_endroit_supplement: e.target.value })}
                    className="h-8 text-sm w-28 bg-white"
                  />
                  <span className="text-xs text-amber-700">$ — selon difficulté d'accès, manœuvres, etc.</span>
                </div>
              </div>
            )}

            {/* ✅ Point 3 : Prix de livraison explicite */}
            {item.prix_livraison > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 flex items-center justify-between text-xs" data-testid={`livraison-affiche-${idx}`}>
                <div className="text-blue-900">
                  <span className="font-semibold">🚚 Frais livraison ({(item.livraison_sens || 'aller') === 'aller_retour' ? 'aller-retour' : 'aller seul.'}) : </span>
                  {item.livraison_km_montant > 0 && <span>{item.livraison_km_montant.toFixed(2)}$ ({item.livraison_km})</span>}
                  {item.livraison_endroit_montant > 0 && (
                    <span className="ml-1">
                      + {item.livraison_endroit_montant.toFixed(2)}$
                      <span className="text-blue-600"> ({item.livraison_endroit === 'precise' ? 'endroit précis' : 'devant adresse'})</span>
                    </span>
                  )}
                </div>
                <strong className="text-blue-900">{item.prix_livraison.toFixed(2)} $</strong>
              </div>
            )}
            </>
            )}
            </>
          )}
        </div>
      )}

      {/* ✅ Point 5 : Extras avec descriptions et montants */}
      {item.type_location && (
        <div className="bg-white border border-slate-200 rounded p-2 space-y-2" data-testid={`extras-section-${idx}`}>
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-700">
              ✨ Extras / Frais supplémentaires {(item.extras || []).length > 0 && <span className="text-slate-400">({(item.extras || []).length})</span>}
            </p>
            <Button
              type="button" size="sm" variant="outline"
              className="h-6 text-[10px] border-purple-300 text-purple-700"
              onClick={() => onPatch({ extras: [...(item.extras || []), { description: '', montant: '' }] })}
              data-testid={`add-extra-${idx}`}
            >
              <Plus className="w-2.5 h-2.5 mr-0.5" /> Ajouter un extra
            </Button>
          </div>
          {(item.extras || []).length === 0 ? (
            <p className="text-[11px] text-slate-400 italic py-1">
              Ex: « Déplacement mi-location pour changement d'emplacement », « Ramassage le soir », etc.
            </p>
          ) : (
            <div className="space-y-1.5">
              {(item.extras || []).map((ex, eIdx) => (
                <div key={eIdx} className="flex items-center gap-2">
                  <Input
                    placeholder="Description (ex: déplacement mi-location)"
                    value={ex.description || ''}
                    onChange={(e) => {
                      const newExtras = [...(item.extras || [])];
                      newExtras[eIdx] = { ...newExtras[eIdx], description: e.target.value };
                      onPatch({ extras: newExtras });
                    }}
                    className="h-8 text-xs flex-1"
                    data-testid={`extra-desc-${idx}-${eIdx}`}
                  />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={ex.montant || ''}
                    onChange={(e) => {
                      const newExtras = [...(item.extras || [])];
                      newExtras[eIdx] = { ...newExtras[eIdx], montant: e.target.value };
                      onPatch({ extras: newExtras });
                    }}
                    className="h-8 text-xs w-24"
                    data-testid={`extra-montant-${idx}-${eIdx}`}
                  />
                  <span className="text-xs text-slate-500">$</span>
                  <Button
                    type="button" size="sm" variant="ghost"
                    className="h-7 w-7 p-0 text-red-400"
                    onClick={() => onPatch({ extras: (item.extras || []).filter((_, i) => i !== eIdx) })}
                    data-testid={`remove-extra-${idx}-${eIdx}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {item.extras_total > 0 && (
                <div className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-2 py-1 flex justify-between">
                  <span className="font-semibold">Total extras</span>
                  <strong>{item.extras_total.toFixed(2)} $</strong>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Bandeau disponibilité ─── */}
      {dispo && dispo.resultats && dispo.resultats.length > 0 && (() => {
        // 🆕 [8.1] Sémantique nuancée — distingue conflit sur item choisi vs autres équipements occupés
        const hasItemConflict = dispo.resultats.some(r => r['100_pct_libre'] === false || (r.qte_disponible !== undefined && r.qte_disponible < r.qte_demandee));
        const nacelleResult = dispo.resultats.find(r => r.type === 'nacelle');
        const otherNacellesBusy = nacelleResult && nacelleResult.qte_totale_parc > 0 && nacelleResult.qte_libre_dans_parc < nacelleResult.qte_totale_parc;
        const variant = hasItemConflict ? 'red' : (otherNacellesBusy ? 'amber' : 'emerald');
        const banners = {
          red: { bg: 'bg-red-50 border-red-300', text: 'text-red-800', label: '⚠️ Conflit sur l\'équipement sélectionné' },
          amber: { bg: 'bg-amber-50 border-amber-300', text: 'text-amber-800', label: '✅ Choisi disponible — d\'autres équipements occupés' },
          emerald: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', label: '✅ Tout disponible pour cette période' },
        }[variant];
        return (
          <div className={`rounded p-2 border text-xs ${banners.bg}`} data-testid={`dispo-banner-${idx}`}>
            <p className={`font-bold text-xs ${banners.text}`}>{banners.label}</p>
            <div className="mt-1 space-y-0.5">
              {dispo.resultats.map((r, ri) => (
                <div key={ri} className={`flex items-start justify-between gap-2 ${!r.ok ? 'text-red-700' : 'text-emerald-700'}`}>
                  <span>
                    <strong>{r.nom || r.piece_nom || r.id}</strong>
                    {r.numero_unite ? <span className="text-slate-500 ml-1">#{r.numero_unite}</span> : null}
                    {r.type === 'nacelle' && r.qte_totale_parc != null ? (
                      <span className="text-slate-500 ml-1">— {r.qte_libre_dans_parc}/{r.qte_totale_parc} nacelle(s) 100% libre(s)</span>
                    ) : (
                      <> — {r.qte_disponible}/{r.qte_totale} dispo</>
                    )}
                    {r.qte_occupee > 0 && <span className="text-slate-500 ml-1">({r.qte_occupee} déjà utilisé{r.qte_occupee > 1 ? 's' : ''})</span>}
                  </span>
                  {r.conflits && r.conflits.length > 0 && (
                    <details className="text-[10px] cursor-pointer">
                      <summary className="underline">Voir conflits</summary>
                      <div className="bg-white border border-slate-200 rounded p-1 mt-1 min-w-[200px] text-slate-700">
                        {r.conflits.map((c, ci) => (
                          <div key={ci} className="leading-tight">
                            • {c.label} — {c.qte} unité{c.qte > 1 ? 's' : ''}
                            {c.date && <span className="text-slate-400 ml-1">({c.date})</span>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
            {/* 🆕 [8.1] Alternatives libres si la nacelle choisie a un conflit */}
            {nacelleResult && nacelleResult['100_pct_libre'] === false && (
              <div className="mt-2 pt-2 border-t border-red-200">
                {(nacelleResult.alternatives_libres || []).length > 0 ? (
                  <>
                    <p className="font-semibold text-slate-700 mb-1">🔄 Alternatives libres pour toute la période :</p>
                    <ul className="space-y-0.5">
                      {nacelleResult.alternatives_libres.map(a => (
                        <li key={a.id} className="text-emerald-700">• {a.nom} {a.numero_unite ? <span className="text-slate-500">#{a.numero_unite}</span> : null}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-red-700 font-semibold">Aucune nacelle libre pour cette période — change la période ou la nacelle.</p>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ─── Total de la ligne (résumé clair) ─── */}
      {item.type_location && (
        <div className="pt-2 border-t border-slate-200 space-y-1">
          <div className="space-y-0.5 text-xs text-slate-600">
            {item.prix_location > 0 && (
              <div className="flex justify-between">
                <span>Location ({(item.quantite || 1)} × {(item.prix_location || 0).toFixed(2)}$)</span>
                <span className="font-medium">{((item.prix_location || 0) * (item.quantite || 1)).toFixed(2)}$</span>
              </div>
            )}
            {item.prix_livraison > 0 && (
              <div className="flex justify-between">
                <span>Livraison</span>
                <span className="font-medium">{item.prix_livraison.toFixed(2)}$</span>
              </div>
            )}
            {item.extras_total > 0 && (
              <div className="flex justify-between">
                <span>Extras ({(item.extras || []).length})</span>
                <span className="font-medium">{item.extras_total.toFixed(2)}$</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between border-t pt-1.5">
            <span className="text-sm font-semibold text-slate-700">Total ligne</span>
            <span className="font-bold text-lg text-slate-800" data-testid={`total-ligne-${idx}`}>
              {(item.montant || 0).toFixed(2)} $
            </span>
          </div>
        </div>
      )}

      {/* Modal d'inspection retirée du contrat — voir widget Vue d'ensemble */}
    </div>
  );
}
